import React, { useState } from 'react';
import PlannerIcons from '../../components/shared/PlannerIcons';

export default function ProjectVIPs({ project, updateProject }) {
  const [newVip, setNewVip] = useState({ name: '', role: 'Maid of Honor', phone: '', email: '', notes: '' });
  
  const vips = project.vips || [];

  const roles = [
    'Maid of Honor', 'Best Man', 'Bridesmaid', 'Groomsman', 
    'Mother of Bride', 'Father of Bride', 'Mother of Groom', 'Father of Groom', 
    'Ring Bearer', 'Flower Girl', 'Usher', 'Reader', 'Officiant', 'Other'
  ];

  const handleAddVip = (e) => {
    e.preventDefault();
    if (!newVip.name.trim()) return;

    updateProject(project.id, {
      vips: [...vips, { ...newVip, id: Date.now().toString() }]
    });
    
    setNewVip({ name: '', role: 'Maid of Honor', phone: '', email: '', notes: '' });
  };

  const handleRemoveVip = (id) => {
    if (window.confirm("Remove this VIP from the list?")) {
      updateProject(project.id, {
        vips: vips.filter(v => v.id !== id)
      });
    }
  };

  return (
    <div className="space-y-6 md:space-y-8 animate-fade-in">
        {/* Header */}
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6">
            <div>
                <h2 className="text-2xl font-bold font-serif text-gray-900">VIPs & Wedding Party</h2>
                <p className="text-sm text-gray-500 mt-1">Manage contact info and roles for the bridal party and family.</p>
            </div>
            <div className="bg-pink-50 px-4 py-3 rounded-xl border border-pink-100 flex-1 sm:flex-none text-center sm:text-left">
                <p className="text-[10px] font-bold text-pink-600 uppercase tracking-widest mb-1">Total VIPs</p>
                <p className="text-2xl font-black text-pink-700">{vips.length}</p>
            </div>
        </div>

        {/* Add VIP Form */}
        <div className="bg-white p-4 md:p-6 rounded-xl border border-gray-200 shadow-sm">
            <h3 className="text-sm font-bold text-gray-900 mb-4 uppercase tracking-wider">Add New VIP</h3>
            <form onSubmit={handleAddVip} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 items-start">
                <div className="lg:col-span-2">
                    <label className="text-xs font-semibold text-gray-600 mb-1 block">Name</label>
                    <input 
                        type="text" 
                        required
                        value={newVip.name} 
                        onChange={e => setNewVip({...newVip, name: e.target.value})} 
                        placeholder="Full Name" 
                        className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-pink-500 outline-none"
                    />
                </div>
                <div className="lg:col-span-1">
                    <label className="text-xs font-semibold text-gray-600 mb-1 block">Role</label>
                    <select 
                        value={newVip.role} 
                        onChange={e => setNewVip({...newVip, role: e.target.value})} 
                        className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-pink-500 outline-none bg-white"
                    >
                        {roles.map(role => <option key={role} value={role}>{role}</option>)}
                    </select>
                </div>
                <div className="lg:col-span-1">
                    <label className="text-xs font-semibold text-gray-600 mb-1 block">Phone (Opt)</label>
                    <input 
                        type="tel" 
                        value={newVip.phone} 
                        onChange={e => setNewVip({...newVip, phone: e.target.value})} 
                        placeholder="Phone" 
                        className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-pink-500 outline-none"
                    />
                </div>
                <div className="lg:col-span-1">
                    <label className="text-xs font-semibold text-gray-600 mb-1 block">Email (Opt)</label>
                    <input 
                        type="email" 
                        value={newVip.email} 
                        onChange={e => setNewVip({...newVip, email: e.target.value})} 
                        placeholder="Email" 
                        className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-pink-500 outline-none"
                    />
                </div>
                <div className="lg:col-span-1 flex items-end h-full">
                    <button 
                        type="submit" 
                        disabled={!newVip.name.trim()}
                        className="w-full bg-gray-900 hover:bg-black text-white px-4 py-2.5 rounded-lg font-bold transition shadow-md disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2 h-[42px]"
                    >
                        <PlannerIcons.Plus /> Add
                    </button>
                </div>
            </form>
        </div>

        {/* VIP Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {vips.map((vip) => (
                <div key={vip.id} className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm relative group hover:border-pink-300 transition-colors flex flex-col">
                    <div className="absolute top-4 right-4">
                        <button
                            onClick={() => handleRemoveVip(vip.id)}
                            className="p-1.5 text-gray-400 hover:text-red-500 bg-gray-50 hover:bg-red-50 rounded-lg transition opacity-0 group-hover:opacity-100"
                            title="Remove VIP"
                        >
                            <PlannerIcons.Trash />
                        </button>
                    </div>

                    <span className="text-[10px] font-bold uppercase tracking-wider text-pink-700 bg-pink-50 border border-pink-100 px-2.5 py-1 rounded-full w-fit mb-3">
                        {vip.role}
                    </span>
                    
                    <h4 className="font-bold text-gray-900 text-lg pr-8 mb-4">{vip.name}</h4>
                    
                    <div className="space-y-2 mt-auto">
                        {vip.phone && (
                            <a href={`tel:${vip.phone}`} className="flex items-center gap-2 text-sm text-gray-600 hover:text-pink-600 transition">
                                <span>📱</span> {vip.phone}
                            </a>
                        )}
                        {vip.email && (
                            <a href={`mailto:${vip.email}`} className="flex items-center gap-2 text-sm text-gray-600 hover:text-pink-600 transition truncate">
                                <span>✉️</span> {vip.email}
                            </a>
                        )}
                        {!vip.phone && !vip.email && (
                            <p className="text-sm text-gray-400 italic">No contact info provided</p>
                        )}
                    </div>

                    {vip.notes && (
                        <div className="mt-4 pt-3 border-t border-gray-100">
                            <p className="text-xs text-gray-500 bg-gray-50 p-2 rounded-lg leading-relaxed">
                                {vip.notes}
                            </p>
                        </div>
                    )}
                </div>
            ))}
            {vips.length === 0 && (
                <div className="col-span-full py-16 text-center text-gray-400 border-2 border-dashed border-gray-200 rounded-xl bg-gray-50/50">
                    <div className="text-4xl mb-3">🎩</div>
                    <p className="font-medium text-gray-500">No VIPs added yet.</p>
                    <p className="text-sm mt-1">Add your wedding party and immediate family to keep their details handy.</p>
                </div>
            )}
        </div>
    </div>
  );
}